/**
 * Settings page - Admin only.
 * System information, n8n webhook URL, tracked Google Sheets, data export.
 */

import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isAdmin } from '../utils/permissions';
import {
  exportD1,
  fetchAdminSetting,
  putAdminSetting,
  type AdminSetting,
} from '../utils/api';
import TrackedSheetsManager from '../components/settings/TrackedSheetsManager';

const N8N_WEBHOOK_KEY = 'n8n.webhook_url';

export default function Settings() {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<string | null>(null);

  // n8n webhook URL state
  const [webhookSetting, setWebhookSetting] = useState<AdminSetting | null>(null);
  const [webhookInput, setWebhookInput] = useState('');
  const [webhookLoading, setWebhookLoading] = useState(true);
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [webhookStatus, setWebhookStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !isAdmin(user.role)) return;
    let cancelled = false;
    (async () => {
      try {
        const setting = await fetchAdminSetting(N8N_WEBHOOK_KEY);
        if (cancelled) return;
        setWebhookSetting(setting);
        setWebhookInput(typeof setting.value === 'string' ? setting.value : '');
      } catch {
        // 404 = not configured yet
      } finally {
        if (!cancelled) setWebhookLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!user || !isAdmin(user.role)) {
    return <Navigate to="/" replace />;
  }

  async function handleExport() {
    setExporting(true);
    setExportStatus(null);
    try {
      const result = await exportD1();
      setExportStatus(
        `Export successful! File: ${result.filePath} (${Math.round(result.fileSize / 1024)} KB)`,
      );
    } catch (err) {
      setExportStatus(`Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  }

  async function handleSaveWebhook(e: React.FormEvent) {
    e.preventDefault();
    setWebhookSaving(true);
    setWebhookStatus(null);
    try {
      const trimmed = webhookInput.trim();
      if (!trimmed) {
        setWebhookStatus('URL cannot be empty.');
        return;
      }
      const saved = await putAdminSetting(N8N_WEBHOOK_KEY, trimmed, 'n8n hub webhook');
      setWebhookSetting(saved);
      setWebhookStatus('Saved.');
    } catch (err) {
      setWebhookStatus(`Save failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setWebhookSaving(false);
    }
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">System configuration and data management</p>
      </header>

      <section className="card">
        <h2 className="card-title">System Information</h2>
        <dl className="info-list">
          <dt>Current User</dt>
          <dd>{user.name}</dd>

          <dt>Role</dt>
          <dd className="role-badge">{user.role}</dd>

          <dt>Email</dt>
          <dd>{user.email}</dd>
        </dl>
      </section>

      <section className="card">
        <h2 className="card-title">n8n Webhook URL</h2>
        <p className="card-text">
          The single webhook for all module automations. When you click sync on any module, the
          worker fires this URL with a <code>route</code> discriminator; n8n switches on it and
          calls back when done.
        </p>
        {webhookLoading ? (
          <p className="loading-text">Loading…</p>
        ) : (
          <form
            onSubmit={handleSaveWebhook}
            style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 600 }}
          >
            <input
              type="url"
              value={webhookInput}
              onChange={(e) => setWebhookInput(e.target.value)}
              placeholder="https://your-n8n.example.com/webhook/homebase-hub"
              style={{
                padding: '8px 10px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.9rem',
              }}
            />
            {webhookSetting && (
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', margin: 0 }}>
                Last updated {new Date(webhookSetting.updated_at + 'Z').toLocaleString()} by{' '}
                {webhookSetting.updated_by}
              </p>
            )}
            <div>
              <button type="submit" className="button button-primary" disabled={webhookSaving}>
                {webhookSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
            {webhookStatus && (
              <p
                className={`export-status ${webhookStatus.includes('failed') ? 'error' : 'success'}`}
              >
                {webhookStatus}
              </p>
            )}
          </form>
        )}
      </section>

      <TrackedSheetsManager />

      <section className="card">
        <h2 className="card-title">Data Export</h2>
        <p className="card-text">
          Export all data from the D1 database to R2 for backup purposes. This creates a JSON dump
          of all tables.
        </p>
        <button
          className="button button-primary"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? 'Exporting...' : 'Export Database'}
        </button>
        {exportStatus && (
          <p
            className={`export-status ${
              exportStatus.includes('failed') ? 'error' : 'success'
            }`}
          >
            {exportStatus}
          </p>
        )}
      </section>

      <section className="card">
        <h2 className="card-title">Module Management</h2>
        <p className="card-text">
          Module activation and configuration will be available here once modules are built.
        </p>
      </section>
    </div>
  );
}
